import DropdownMenu from "@/components/ui/new-dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAppContext } from "@/contexts/AppContext";
import { cn } from "@/lib/utils";
import {
  CaretDownIcon,
  CheckIcon,
  FolderIcon,
  PencilSimpleIcon,
  PlusIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useCallback, useState } from "react";
import { api } from "@/trpc/react";
import { toast } from "sonner";
import { Project } from "@prisma/client";
import { useRouter } from "next/navigation";
import { RenameProjectModal } from "@/components/modals/rename-project-modal";
import { DeleteProjectModal } from "@/components/modals/delete-project-modal";
import { AddProjectModal } from "@/components/add-project-modal";

export default function ProjectSelector({
  isCollapsed,
}: {
  isCollapsed: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { currentProject, projects } = useAppContext();
  const currentProjectId = currentProject?.id;
  const router = useRouter();

  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addProjectModalOpen, setAddProjectModalOpen] = useState(false);
  const [projectToAction, setProjectToAction] = useState<Project | null>(null);

  const utils = api.useUtils();
  const { mutate: renameProject, isPending: isRenamePending } =
    api.projects.update.useMutation({
      onSuccess: async (updatedProject) => {
        toast.success(`Project "${updatedProject.name}" renamed successfully.`);
        await utils.projects.invalidate();
        setRenameModalOpen(false);
        setProjectToAction(null);
      },
      onError: (error) => {
        toast.error(`Failed to rename project: ${error.message}`);
      },
    });

  const { mutate: deleteProject, isPending: isDeleting } =
    api.projects.delete.useMutation({
      onSuccess: async (deletedProject) => {
        toast.success(`Project "${deletedProject.name}" deleted successfully.`);
        await utils.projects.invalidate();
        setDeleteModalOpen(false);
        setProjectToAction(null);
        if (deletedProject.id === currentProjectId) {
          const remainingProjects = projects.filter(
            (p) => p.id !== deletedProject.id,
          );
          if (remainingProjects.length > 0) {
            router.push(`/projects/${remainingProjects[0]!.id}/api-key`);
          } else {
            router.push("/dashboard");
          }
        }
      },
      onError: (error) => {
        toast.error(`Failed to delete project: ${error.message}`);
      },
    });

  const { mutateAsync: createProject, isPending: isCreateProjectPending } =
    api.projects.create.useMutation({
      onSuccess: async (createdProject: Project) => {
        await utils.projects.invalidate();
        setAddProjectModalOpen(false);
        toast(`Project "${createdProject.name}" has been created.`, {
          icon: <CheckIcon className="h-4 w-4" />,
        });
        router.push("/dashboard");
      },
      onError: (error) => {
        toast.error(`Failed to create project: ${error.message}`);
      },
    });

  const findProject = useCallback(
    (projectId: string): Project | undefined =>
      projects.find((p) => p.id === projectId),
    [projects],
  );

  const triggerRename = (e: Event, projectId: string) => {
    e.stopPropagation();
    const project = findProject(projectId);
    if (project) {
      setProjectToAction(project);
      setRenameModalOpen(true);
    }
  };

  const triggerDelete = (e: Event, projectId: string) => {
    e.stopPropagation();
    const project = findProject(projectId);
    if (project) {
      setProjectToAction(project);
      setDeleteModalOpen(true);
    }
  };

  const onRenameSubmit = useCallback(
    (newName: string) => {
      if (projectToAction) {
        renameProject({ id: projectToAction.id, name: newName });
      }
    },
    [projectToAction],
  );

  const onDeleteConfirm = useCallback(() => {
    if (projectToAction) {
      // Prevent deletion of the last project
      if (projects.length <= 1) {
        toast.error(
          "Cannot delete your last project. You must have at least one project in your account. Create a new project before deleting this one.",
          { duration: 5000 },
        );
        setDeleteModalOpen(false);
        setProjectToAction(null);
        return;
      }

      deleteProject({ id: projectToAction.id });
    }
  }, [projectToAction, projects.length, deleteProject]);

  const menuItems = [
    ...projects.map((project) => ({
      text: project.name,
      icon: <FolderIcon />,
      url: `/projects/${project.id}/api-key`,
      type: "link",
      isSelected: project.id === currentProjectId,
      hoverActions: [
        {
          icon: <PencilSimpleIcon size={16} />,
          onClick: () => triggerRename(new Event(""), project.id),
          tooltip: "Edit",
        },
        {
          icon: <XIcon size={16} />,
          onClick: () => triggerDelete(new Event(""), project.id),
          tooltip: "Delete",
        },
      ],
    })),
    {
      text: "Add New Project",
      icon: <PlusIcon size={16} />,
      type: "button",
      divider: true,
      onClick: () => setAddProjectModalOpen(true),
    },
  ];

  return (
    <>
      <DropdownMenu
        menuItems={menuItems}
        side={isCollapsed ? "right" : "bottom"}
        className={cn(isCollapsed ? "mt-[100px]" : "ml-5")}
      >
        <div
          onClick={() => setIsOpen((prev) => !prev)}
          className={cn(
            "flex items-center justify-between h-spacing-8 rounded-sm gap-spacing-2 px-spacing-3 transition-colors hover:bg-palette-surface-container",
          )}
        >
          <div className="flex gap-spacing-3 items-center min-w-0">
            {isCollapsed ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <FolderIcon className="h-5 w-5 shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="right" className="ml-2" sideOffset={8}>
                  {currentProject?.name}
                </TooltipContent>
              </Tooltip>
            ) : (
              <FolderIcon className="h-5 w-5 shrink-0" />
            )}
            <span
              className={cn(
                "pb-[2px] text-palette-text-primary text-sm font-medium font-sans transition-all duration-300 ease-in-out inline-block truncate",
                isCollapsed
                  ? "opacity-0 scale-90 w-0"
                  : "opacity-100 scale-100 w-auto",
              )}
            >
              {currentProject?.name}
            </span>
          </div>
          <CaretDownIcon
            className={`text-palette-icon-default transition-transform ${isOpen ? "rotate-180" : ""}`}
            size={16}
          />
        </div>
      </DropdownMenu>
      {projectToAction && (
        <RenameProjectModal
          isOpen={renameModalOpen}
          onOpenChange={setRenameModalOpen}
          project={projectToAction}
          onSubmit={onRenameSubmit}
          isPending={isRenamePending}
        />
      )}
      {projectToAction && (
        <DeleteProjectModal
          isOpen={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          project={projectToAction}
          onConfirm={onDeleteConfirm}
          isPending={isDeleting}
        />
      )}
      <AddProjectModal
        isOpen={addProjectModalOpen}
        onOpenChange={setAddProjectModalOpen}
        onSubmit={createProject}
        isPending={isCreateProjectPending}
      />
    </>
  );
}
